// 성능 최적화를 위한 메모이제이션 유틸리티 함수

import { useCallback, useEffect, useRef, useState } from 'react';

// 컴포넌트 리렌더링 방지를 위한 디바운스 훅
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// 이벤트 핸들러 최적화를 위한 쓰로틀 훅
export const useThrottle = (callback, delay) => {
  const lastRan = useRef(Date.now());

  return useCallback(
    (...args) => {
      const now = Date.now();
      if (now - lastRan.current >= delay) {
        callback(...args);
        lastRan.current = now;
      }
    },
    [callback, delay]
  );
};

// 이미지 지연 로딩을 위한 훅
export const useLazyLoading = (elementRef, options = {}) => {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    // IntersectionObserver가 지원되지 않는 브라우저를 위한 폴백
    if (!('IntersectionObserver' in window)) {
      setIntersecting(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIntersecting(entry.isIntersecting);
      },
      { threshold: 0.1, ...options }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [elementRef, options]);

  return isIntersecting;
};

// 로컬 스토리지 접근을 위한 유틸리티 함수
export const storage = {
  get: (key, defaultValue = null) => {
    try {
      // localStorage가 지원되는지 확인
      if (typeof window === 'undefined' || !window.localStorage) {
        return defaultValue;
      }
      
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Error getting item ${key} from localStorage`, error);
      return defaultValue;
    }
  },
  set: (key, value) => {
    try {
      // localStorage가 지원되는지 확인
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error(`Error setting item ${key} to localStorage`, error);
      return false;
    }
  },
  remove: (key) => {
    try {
      // localStorage가 지원되는지 확인
      if (typeof window === 'undefined' || !window.localStorage) {
        return false;
      }
      
      window.localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Error removing item ${key} from localStorage`, error);
      return false;
    }
  }
};

// 브라우저 호환성 체크 유틸리티
export const browserCompatibility = {
  // 개선된 WebP 지원 체크 - 더 안정적인 방식 사용
  checkWebpSupport: () => {
    // 폴백: 항상 지원한다고 가정
    if (typeof window === 'undefined' || !window.createImageBitmap) {
      return Promise.resolve(true);
    }
    
    return new Promise(resolve => {
      try {
        const webpData = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
        fetch(webpData)
          .then(response => response.blob())
          .then(blob => {
            // createImageBitmap이 지원되는 경우 WebP 지원 여부 확인
            createImageBitmap(blob)
              .then(() => resolve(true))
              .catch(() => resolve(false));
          })
          .catch(() => resolve(true)); // 오류 발생 시 기본적으로 지원한다고 가정
      } catch (e) {
        // 예외 발생 시 기본적으로 지원한다고 가정
        resolve(true);
      }
    });
  },
  
  // 개선된 터치 지원 체크 - 다양한 방식으로 확인
  checkTouchSupport: () => {
    try {
      if (typeof window === 'undefined') {
        return true;
      }
      
      return (
        ('ontouchstart' in window) ||
        (window.DocumentTouch && document instanceof window.DocumentTouch) ||
        (navigator.maxTouchPoints > 0) ||
        (navigator.msMaxTouchPoints > 0) ||
        (window.matchMedia && window.matchMedia('(pointer:coarse)').matches)
      );
    } catch (e) {
      // 예외 발생 시 기본적으로 지원한다고 가정
      return true;
    }
  },
  
  // 개선된 로컬 스토리지 지원 체크 - 더 안전한 방식 사용
  checkLocalStorageSupport: () => {
    try {
      if (typeof window === 'undefined') {
        return true;
      }
      
      // localStorage 객체가 존재하는지 확인
      if (!window.localStorage) {
        return false;
      }
      
      // 실제로 데이터를 저장하고 읽을 수 있는지 확인
      const testKey = '__test_storage__';
      localStorage.setItem(testKey, testKey);
      const result = localStorage.getItem(testKey) === testKey;
      localStorage.removeItem(testKey);
      return result;
    } catch (e) {
      // 예외 발생 시 (예: 개인정보 보호 모드에서는 localStorage 접근 시 예외 발생)
      return false;
    }
  },
  
  // 브라우저 기능 감지 대신 사용자 에이전트 기반 폴백 제공
  isMobileBrowser: () => {
    try {
      if (typeof window === 'undefined' || !window.navigator) {
        return false;
      }
      
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      return (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(ua) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(ua.substr(0, 4))
      );
    } catch (e) {
      return false;
    }
  }
};

// 유틸리티 객체 내보내기
const optimizationUtils = {
  useDebounce,
  useThrottle,
  useLazyLoading,
  storage,
  browserCompatibility
};

export default optimizationUtils;
